import * as React from 'react';
import { styled } from '@linaria/react';
import { Play } from 'lucide-react';

import Space from '@/components/Space';
import ButtonPrimary from '@/components/ButtonPlayful/ButtonPrimary';

import AnimatedBlock, { DISPLACEMENT } from './AnimatedBlock';

const EASING_FUNCTIONS = {
  'ease-in': {
    label: 'ease-in',
    value: 'ease-in',
    duration: 800,
  },
  'ease-out': {
    label: 'ease-out',
    value: 'ease-out',
    duration: 800,
  },
  'ease-in-out': {
    label: 'ease-in-out',
    value: 'ease-in-out',
    duration: 800,
  },
  ease: {
    label: 'ease',
    value: 'ease',
    duration: 800,
  },
  spring: {
    label: 'spring',
    value:
      'linear(0, 0.001 0.2%, 0.009 0.5%, 0.022 0.8%, 0.04 1.1%, 0.091 1.7%, 0.172 2.4%, 0.347 3.6%, 0.876 6.8%, 1.003 7.7%, 1.111 8.6%, 1.197 9.5%, 1.253 10.3%, 1.275 10.7%, 1.295 11.2%, 1.307 11.6%, 1.315 12.1%, 1.317 12.4%, 1.317 12.7%, 1.315 13%, 1.31 13.4%, 1.293 14.1%, 1.269 14.8%, 1.21 16.1%, 1.037 19.4%, 0.993 20.4%, 0.96 21.3%, 0.936 22.1%, 0.919 22.9%, 0.907 23.7%, 0.9 24.6%, 0.899 25.2%, 0.901 25.9%, 0.906 26.6%, 0.914 27.3%, 0.932 28.6%, 0.989 32%, 1.012 33.8%, 1.025 35.4%, 1.032 37.1%, 1.032 38.2%, 1.029 39.4%, 0.998 45.8%, 0.992 47.7%, 0.99 49.6%, 0.991 52%, 1.001 58.3%, 1.003 62%, 0.999 74.2%, 1)',
    duration: 1600,
  },
  bounce: {
    label: 'bounce',
    value:
      'linear(0, 0.151 8.1%, 0.223 11.7%, 0.304 15.2%, 0.392 18.4%, 0.497 21.6%, 0.619 24.8%, 0.752 27.9%, 0.999 33.3%, 0.842 37.1%, 0.79 38.6%, 0.748 40%, 0.714 41.4%, 0.691 42.7%, 0.677 44%, 0.673 44.7%, 0.672 45.3%, 0.676 46.5%, 0.69 47.8%, 0.712 49.1%, 0.743 50.4%, 0.824 53%, 0.999 57.7%, 0.927 60%, 0.883 61.8%, 0.867 62.7%, 0.856 63.6%, 0.85 64.4%, 0.848 65.3%, 0.849 66.1%, 0.855 67%, 0.865 67.9%, 0.879 68.8%, 0.911 70.5%, 0.999 74.5%, 0.97 76.2%, 0.953 77.5%, 0.943 78.8%, 0.94 80.2%, 0.942 81.4%, 0.95 82.7%, 0.989 86.9%, 1 88.2%, 0.99 90%, 0.987 91.9%, 0.989 93.5%, 0.998 97.5%, 1)',
    duration: 1200,
  },
};

interface Props {
  functions: Array<keyof typeof EASING_FUNCTIONS>;
}

const BALL_WIDTH = 48;

function EasingComparisonDemo({ functions }: Props) {
  const [side, setSide] = React.useState<'left' | 'right'>('left');

  return (
    <Wrapper>
      <Rows>
        {functions.map((functionName) => {
          const {
            label,
            value,
            duration = 800,
          } = EASING_FUNCTIONS[functionName];

          return (
            <Row key={functionName}>
              <Label>{label}</Label>
              <AnimatedBlock
                style={{
                  transform: `translateX(${side === 'left' ? 0 : DISPLACEMENT}px)`,
                  transition: `transform ${duration}ms ${value}`,
                }}
              />
            </Row>
          );
        })}
      </Rows>
      <Actions>
        <ActionButton
          Icon={Play}
          onClick={() => setSide(side === 'left' ? 'right' : 'left')}
        >
          Run Animation
        </ActionButton>
      </Actions>
    </Wrapper>
  );
}

const Wrapper = styled(Space)`
  width: 100%;
  max-width: 500px;
  margin-inline: auto;
  margin-bottom: 48px;
  border: var(--page-border);

  @media (max-width: 25rem) {
    width: calc(100% + var(--viewport-padding) * 2);
    max-width: revert;
    margin-inline: calc(var(--viewport-padding) * -1);
    overflow: hidden;
  }
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`;

const Row = styled.div`
  display: flex;
  gap: 32px;

  @media (max-width: 37.5rem) {
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
`;

const Label = styled.p`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 8rem;
  font-family: var(--font-family-mono);
  font-size: 1rem;

  @media (max-width: 37.5rem) {
    width: revert;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  margin-top: 16px;
  border-top: 1px solid var(--color-gray-200);
`;

const ActionButton = styled(ButtonPrimary)`
  width: 300px;
  max-width: 100%;
`;

export default EasingComparisonDemo;
